import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/** @extends {Component<{status: HTMLElement}>} */
class AtelierCardOptions extends Component {
  connectedCallback() {
    super.connectedCallback();
    this._initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this._cleanup();
    this._initialize();
  }

  disconnectedCallback() {
    this._cleanup();
    super.disconnectedCallback();
  }

  _initialize() {
    this._card = this.closest('[data-atelier-product-card]');
    if (!(this._card instanceof HTMLElement)) return;
    this._abort = new AbortController();
    const options = { signal: this._abort.signal };
    this._card.addEventListener(StandardEvents.productSelect, () => this._clearPreview(), options);
    this._card.addEventListener(SlideshowSelectEvent.eventName, (event) => {
      if (event.detail?.userInitiated) this._clearPreview();
    }, options);
  }

  /** @param {MouseEvent} event */
  async selectColor(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-preview-color]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || !this._card) return;
    event.preventDefault();
    event.stopPropagation();
    this._loading?.abort();
    const controller = new AbortController();
    this._loading = controller;
    const template = button.querySelector('template[data-preview-image]');
    const source = template instanceof HTMLTemplateElement ? template.content.querySelector('img') : null;
    if (!(source instanceof HTMLImageElement)) {
      this._clearPreview();
      this._announce(this.dataset.missingLabel || '');
      return;
    }
    this.setAttribute('aria-busy', 'true');
    try {
      await this._preload(source, controller.signal);
      if (controller.signal.aborted || !this.isConnected) return;
      this._restoreImage();
      const slideshow = this._card.querySelector('.card-gallery slideshow-component');
      let image;
      if (slideshow && button.dataset.mediaId && typeof slideshow.select === 'function') {
        const slide = [...slideshow.querySelectorAll('slideshow-slide')].find((item) => item.getAttribute('slide-id') === button.dataset.mediaId);
        await slideshow.select({ id: button.dataset.mediaId }, undefined, { animate: false });
        if (controller.signal.aborted || !this.isConnected) return;
        if (slide?.getAttribute('aria-hidden') === 'false' && slide.getBoundingClientRect().height > 0) {
          image = slide.querySelector('.product-media__image');
          if (image) image.loading = 'eager';
        }
      }
      if (!(image instanceof HTMLImageElement)) {
        image = this._card.querySelector('.card-gallery slideshow-slide[aria-hidden="false"] .product-media__image') || this._card.querySelector('.card-gallery > a .product-media__image, .atelier-models__image, .resource-card__image:not(.resource-card__image--secondary), .hotspot-dialog__product-image');
        if (!(image instanceof HTMLImageElement)) throw new Error('Missing product image');
        this._originalImage = { image, attributes: new Map([...image.attributes].map((attr) => [attr.name, attr.value])) };
        this._applyImage(image, source);
      }
      this._card.dataset.atelierColorPreview = button.dataset.mediaId || 'true';
      const transition = this._card.closest('[data-product-transition]') || this._card;
      transition.setAttribute('data-featured-media-url', source.src);
      for (const item of this.querySelectorAll('[data-preview-color]')) item.setAttribute('aria-pressed', String(item === button));
      this._updateLinks(button);
      this._announce((this.dataset.updatedLabel || '').replace('[color]', button.dataset.previewColor || ''));
    } catch (error) {
      if (error?.name !== 'AbortError') this._announce(this.dataset.errorLabel || '');
    } finally {
      if (this._loading === controller) {
        this._loading = null;
        this.removeAttribute('aria-busy');
      }
    }
  }

  _preload(source, signal) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const cleanup = () => {
        image.onload = null;
        image.onerror = null;
        signal.removeEventListener('abort', abort);
      };
      const abort = () => {
        cleanup();
        image.removeAttribute('srcset');
        image.removeAttribute('src');
        reject(new DOMException('Aborted', 'AbortError'));
      };
      image.onload = () => { cleanup(); resolve(); };
      image.onerror = () => { cleanup(); reject(new Error('Image load failed')); };
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) { abort(); return; }
      const displayed = this._card?.querySelector('.product-media__image, .atelier-models__image, .resource-card__image');
      image.sizes = displayed?.sizes || '100vw';
      image.srcset = source.srcset;
      image.src = source.src;
    });
  }

  _applyImage(image, source) {
    for (const name of ['srcset', 'src', 'width', 'height', 'alt']) {
      const value = source.getAttribute(name);
      if (value !== null) image.setAttribute(name, value);
      else image.removeAttribute(name);
    }
    image.loading = 'eager';
    image.setAttribute('data_max_resolution', source.src);
    const focalPoint = source.dataset.focalPoint || '50% 50%';
    image.style.setProperty('--focal-point', focalPoint);
    image.style.objectPosition = focalPoint;
  }

  _updateLinks(button) {
    if (!button.dataset.variantId || !button.dataset.variantUrl) return;
    const selected = new URL(button.dataset.variantUrl, window.location.origin);
    for (const link of this._card.querySelectorAll('a[href]')) {
      const url = new URL(link.href);
      if (url.origin !== selected.origin || !url.pathname.includes('/products/')) continue;
      url.pathname = selected.pathname;
      url.searchParams.set('variant', button.dataset.variantId);
      link.href = url.toString();
    }
    for (const quickAdd of this._card.querySelectorAll('quick-add-component[data-renders-both-buttons="true"]')) {
      quickAdd.dataset.quickAddButton = 'choose';
    }
  }

  _restoreImage() {
    const original = this._originalImage;
    if (!original?.image.isConnected) { this._originalImage = null; return; }
    for (const name of ['srcset', 'src', 'width', 'height', 'alt', 'loading', 'style', 'data_max_resolution']) {
      if (original.attributes.has(name)) original.image.setAttribute(name, original.attributes.get(name));
      else original.image.removeAttribute(name);
    }
    this._originalImage = null;
  }

  _clearPreview() {
    this._loading?.abort();
    this._loading = null;
    this.removeAttribute('aria-busy');
    this._restoreImage();
    if (this._card) delete this._card.dataset.atelierColorPreview;
    for (const item of this.querySelectorAll('[data-preview-color]')) item.setAttribute('aria-pressed', 'false');
  }

  _announce(message) {
    if (this.refs.status) this.refs.status.textContent = message;
  }

  _cleanup() {
    this._loading?.abort();
    this._abort?.abort();
    if (this._card) delete this._card.dataset.atelierColorPreview;
    this._loading = null;
    this._abort = null;
    this._originalImage = null;
    this.removeAttribute('aria-busy');
  }
}

if (!customElements.get('atelier-card-options')) customElements.define('atelier-card-options', AtelierCardOptions);
